import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Pressable,
  Modal,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import Header from '../components/Header';
import { getChargerEvents } from '../services/charger-service';
import { isSuccessful } from '../services/api-requests';
import type { RootStackParamList } from '../navigation/stackTypes';
import type { ChargerEventRecord } from '../models/charger-event-model';
import OcppConnectionInstructions from '../components/OcppConnectionInstructions';

const EVENT_LABELS: Record<string, string> = {
  boot: 'Charger online',
  status_change: 'Status change',
  fault: 'Fault',
  fault_recovery: 'Fault cleared',
  diagnostics: 'Diagnostics',
  firmware_update: 'Firmware',
  security: 'Security',
};

const DEFAULT_FETCH_LIMIT = 150;

type Props = NativeStackScreenProps<RootStackParamList, 'ChargerEventLogs'>;

function metaString(m: Record<string, unknown>, key: string): string | undefined {
  const v = m[key];
  if (v === undefined || v === null) return undefined;
  return String(v);
}

function eventTitle(event: ChargerEventRecord): string {
  return EVENT_LABELS[event.event_type] ?? event.event_type.replace(/_/g, ' ');
}

function eventSubtitle(event: ChargerEventRecord): string {
  const m = event.metadata || {};
  const status = metaString(m, 'status');
  const err = metaString(m, 'error_code');
  const userMsg = metaString(m, 'user_message');
  const connector = metaString(m, 'connector_id');
  const parts: string[] = [];
  if (connector !== undefined) parts.push(`Connector ${connector}`);
  if (status) parts.push(status);
  if (err) parts.push(err);
  if (userMsg && event.event_type === 'fault') parts.push(userMsg);
  if (parts.length) return parts.slice(0, 3).join(' · ');
  return '';
}

function isFaultLikeStatus(status: string | undefined): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return s.includes('fault');
}

function eventSeverity(event: ChargerEventRecord): 'error' | 'warning' | 'default' {
  const m = event.metadata || {};
  const sev = metaString(m, 'error_severity')?.toLowerCase();
  if (event.event_type === 'fault' || sev === 'high' || sev === 'critical') return 'error';
  if (event.event_type === 'fault_recovery') return 'default';
  const status = metaString(m, 'status');
  if (event.event_type === 'status_change' && isFaultLikeStatus(status)) return 'error';
  if (event.event_type === 'status_change' && status && /fault|error|unavailable/i.test(status))
    return 'warning';
  if (metaString(m, 'error_code')) return 'warning';
  return 'default';
}

function formatEventTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatMetadataRows(metadata: Record<string, unknown>): { key: string; value: string }[] {
  const keys = Object.keys(metadata).sort((a, b) => a.localeCompare(b));
  return keys.map((key) => {
    const val = metadata[key];
    let value: string;
    if (val === null || val === undefined) value = '—';
    else if (typeof val === 'object') {
      try {
        value = JSON.stringify(val, null, 2);
      } catch {
        value = String(val);
      }
    } else value = String(val);
    return { key, value };
  });
}

function apiErrorMessage(status: number | undefined, payload: unknown): string {
  const msg =
    payload && typeof payload === 'object' && 'message' in payload
      ? String((payload as { message: unknown }).message)
      : undefined;
  if (status === 401) return msg || 'Please sign in again to view charger activity.';
  if (status === 403) return msg || 'Only the charger host can view this activity log.';
  if (status === 400) return msg || 'Invalid request.';
  if (status !== undefined && status >= 500) return msg || 'Server error. Try again later.';
  return msg || 'Unable to load charger activity.';
}

const ChargerEventLogsScreen: React.FC<Props> = ({ route }) => {
  const { chargerId, chargerName } = route.params ?? {};
  const [events, setEvents] = useState<ChargerEventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailEvent, setDetailEvent] = useState<ChargerEventRecord | null>(null);

  const load = useCallback(async () => {
    if (!chargerId) {
      setError('Missing charger.');
      setLoading(false);
      return;
    }
    const response = await getChargerEvents(chargerId, DEFAULT_FETCH_LIMIT);
    if (isSuccessful(response.status) && response.payload?.events) {
      setEvents(response.payload.events);
      setError(null);
    } else {
      const errMsg = apiErrorMessage(response.status, response.payload);
      setEvents([]);
      setError(errMsg);
    }
  }, [chargerId]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      await load();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const renderItem = ({ item }: { item: ChargerEventRecord }) => {
    const severity = eventSeverity(item);
    const accent =
      severity === 'error' ? '#E53E3E' : severity === 'warning' ? '#DD9A3C' : '#39BEB7';
    const sub = eventSubtitle(item);

    return (
      <Pressable
        style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}
        onPress={() => setDetailEvent(item)}
      >
        <View style={[styles.rowAccent, { backgroundColor: accent }]} />
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowTitle}>{eventTitle(item)}</Text>
            <MaterialIcons name="chevron-right" size={22} color="#5A5D62" />
          </View>
          {sub ? <Text style={styles.rowSubtitle} numberOfLines={2}>{sub}</Text> : null}
          <Text style={styles.rowTime}>{formatEventTime(item.timestamp)}</Text>
        </View>
      </Pressable>
    );
  };

  return (
    <SafeAreaView style={styles.safe}>
      <Header headerText="Charger activity" forceBackArrow />
      <View style={styles.inner}>
        {chargerName ? (
          <Text style={styles.chargerName} numberOfLines={2}>
            {chargerName}
          </Text>
        ) : null}
        <Text style={styles.hint}>
          Recent operational events reported by your charger (status, faults, firmware, and more).
        </Text>

        {loading && !refreshing ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color="#39BEB7" />
            <Text style={styles.loadingText}>Loading activity…</Text>
          </View>
        ) : error ? (
          <View style={styles.centered}>
            <MaterialIcons name="error-outline" size={40} color="#E53E3E" />
            <Text style={styles.errorText}>{error}</Text>
            <Pressable style={styles.retryBtn} onPress={onRefresh}>
              <Text style={styles.retryBtnText}>Try again</Text>
            </Pressable>
          </View>
        ) : events.length === 0 ? (
          <ScrollView
            contentContainerStyle={styles.emptyScrollContent}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor="#39BEB7"
                colors={['#39BEB7']}
              />
            }
          >
            <View style={styles.emptyHeader}>
              <MaterialIcons name="timeline" size={40} color="#6B7280" />
              <Text style={styles.emptyTitle}>No activity yet</Text>
              <Text style={styles.emptySub}>
                When your charger connects and reports status changes, entries will show up here.
              </Text>
            </View>
            <Text style={styles.emptyInstructionsHint}>
              Haven't connected your charger yet? Use the details below from your
              charger's interface.
            </Text>
            <OcppConnectionInstructions />
          </ScrollView>
        ) : (
          <FlatList
            data={events}
            keyExtractor={(item) => `evt-${item.id}`}
            renderItem={renderItem}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor="#39BEB7"
                colors={['#39BEB7']}
              />
            }
          />
        )}
      </View>

      <Modal
        visible={detailEvent !== null}
        animationType="slide"
        presentationStyle={Platform.OS === 'web' ? undefined : 'pageSheet'}
        onRequestClose={() => setDetailEvent(null)}
      >
        <SafeAreaView style={styles.modalSafe}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {detailEvent ? eventTitle(detailEvent) : ''}
            </Text>
            <Pressable
              hitSlop={12}
              onPress={() => setDetailEvent(null)}
              accessibilityRole="button"
              accessibilityLabel="Close details"
            >
              <MaterialIcons name="close" size={26} color="#E5E7EB" />
            </Pressable>
          </View>
          <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
            {detailEvent ? (
              <>
                <Text style={styles.modalMetaLine}>
                  {formatEventTime(detailEvent.timestamp)}
                </Text>
                <Text style={styles.modalMetaDim}>Event ID {detailEvent.id}</Text>
                <Text style={styles.sectionLabel}>Details</Text>
                {formatMetadataRows(detailEvent.metadata || {}).map((row) => (
                  <View key={row.key} style={styles.kvRow}>
                    <Text style={styles.kvKey}>{row.key}</Text>
                    <Text style={styles.kvVal} selectable>
                      {row.value}
                    </Text>
                  </View>
                ))}
                <Text style={styles.sectionLabel}>Raw metadata (JSON)</Text>
                <Text style={styles.rawJson} selectable>
                  {JSON.stringify(detailEvent.metadata ?? {}, null, 2)}
                </Text>
              </>
            ) : null}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  inner: {
    flex: 1,
    paddingHorizontal: 16,
  },
  chargerName: {
    fontSize: 20,
    fontWeight: '700',
    color: '#F9FAFB',
    marginTop: 8,
    marginBottom: 6,
  },
  hint: {
    fontSize: 14,
    color: '#9CA3AF',
    lineHeight: 20,
    marginBottom: 16,
  },
  listContent: {
    paddingBottom: 24,
  },
  row: {
    flexDirection: 'row',
    backgroundColor: '#242830',
    borderRadius: 12,
    marginBottom: 10,
    overflow: 'hidden',
  },
  rowAccent: {
    width: 4,
  },
  rowBody: {
    flex: 1,
    paddingVertical: 12,
    paddingRight: 10,
    paddingLeft: 12,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#F3F4F6',
    textTransform: 'capitalize',
  },
  rowSubtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#D1D5DB',
    lineHeight: 18,
  },
  rowTime: {
    marginTop: 8,
    fontSize: 12,
    color: '#8D8E90',
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingBottom: 48,
  },
  loadingText: {
    marginTop: 12,
    color: '#9CA3AF',
    fontSize: 14,
  },
  errorText: {
    marginTop: 12,
    color: '#FCA5A5',
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
  },
  retryBtn: {
    marginTop: 20,
    paddingHorizontal: 22,
    paddingVertical: 12,
    borderRadius: 999,
    backgroundColor: '#39BEB7',
  },
  retryBtnText: {
    color: '#0B1E1D',
    fontWeight: '700',
    fontSize: 14,
  },
  emptyScrollContent: {
    paddingBottom: 32,
  },
  emptyHeader: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
  },
  emptyInstructionsHint: {
    fontSize: 14,
    color: '#9CA3AF',
    lineHeight: 20,
    marginBottom: 12,
  },
  emptyTitle: {
    marginTop: 12,
    fontSize: 18,
    fontWeight: '600',
    color: '#E5E7EB',
  },
  emptySub: {
    marginTop: 8,
    fontSize: 14,
    color: '#9CA3AF',
    textAlign: 'center',
    lineHeight: 20,
  },
  modalSafe: {
    flex: 1,
    backgroundColor: '#111418',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#374151',
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: '#F9FAFB',
    marginRight: 12,
  },
  modalScroll: {
    flex: 1,
  },
  modalScrollContent: {
    padding: 18,
    paddingBottom: 40,
  },
  modalMetaLine: {
    fontSize: 15,
    color: '#E5E7EB',
    marginBottom: 4,
  },
  modalMetaDim: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#39BEB7',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 8,
    marginBottom: 10,
  },
  kvRow: {
    marginBottom: 12,
  },
  kvKey: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 4,
  },
  kvVal: {
    fontSize: 14,
    color: '#F3F4F6',
    lineHeight: 20,
  },
  rawJson: {
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', web: 'monospace' }),
    fontSize: 12,
    color: '#D1D5DB',
    backgroundColor: '#1A1D21',
    padding: 12,
    borderRadius: 8,
    overflow: 'hidden',
  },
});

export default ChargerEventLogsScreen;
